import format from "date-fns/format";

import {
  EmployeeCardEmployeeWithGaps,
  EmployeeCardPositionWithGaps,
  getActualEmployeePositions,
  getEmployeePositionNumberBySlot,
  getEmployeePositionSlotByNumber,
} from "~/entity/Employee/lib/restGaps";
import {
  EmployeeCardUpdateParams,
  EmployeeCardUpdatePosition,
  EmployeeCreateParams,
  EmployeeCreatePosition,
  EmployeeFirePositionsItem,
  EmployeeFirePositionsParams,
} from "~/entity/Employee/model/schema";

import { defaultValueDealer, defaultValuePosition } from "./config";
import { normalizeFormDate } from "./normalizeFormDate";
import {
  IEmployeeProfileForm,
  TDealerFormItem,
  TFileInfo,
  TPositionFormItem,
} from "./types";

const parseDateString = (dateString: string | Date) => {
  if (!dateString) return undefined;
  const str = String(dateString);
  const numericPattern = /^\d+$/;

  if (numericPattern.test(str)) {
    return format(new Date(Number(str)), "yyyy-MM-dd");
  }

  return format(new Date(str), "yyyy-MM-dd");
};

const mapFile = (
  document?: {
    id?: string | number | null;
    link?: string | null;
    name?: string | null;
  } | null,
): TFileInfo | null =>
  document?.id != null
    ? {
        id: String(document.id),
        link: document.link || undefined,
        name: document.name || undefined,
      }
    : null;

const mapDealer = (row: EmployeeCardPositionWithGaps): TDealerFormItem => {
  const dismissalOrder = mapFile(row.dismissalOrder);
  const jobDescription = mapFile(row.jobDescription);
  const dataConsent = mapFile(row.dataConsent);
  const positionOrder = mapFile(row.positionOrder);

  return {
    dealer:
      row.bindingType === "PARTNER"
        ? row.organization
          ? {
              label: row.organization.name || "",
              value: String(row.organizationId ?? ""),
            }
          : null
        : row.dealer
          ? {
              label: row.dealer.dealerName || "",
              value: String(row.dealerId ?? ""),
            }
          : null,
    organizationType: row.bindingType === "PARTNER" ? "PARTNER" : "DEALER",
    instanceId: row.graphqlInstanceId
      ? String(row.graphqlInstanceId)
      : undefined,
    status: row.status || "",
    startDate: normalizeFormDate(row.startDate),
    startOrderNumber: row.startOrderNumber || "",
    endDate: normalizeFormDate(row.endDate),
    endOrderNumber: row.endOrderNumber || "",
    lastStatusUpdateAt: row.lastStatusUpdateAt || "",
    lastStatusUpdateByUser: row.lastStatusUpdateByUser?.fullname || "",
    hired: row.hireByUser?.fullname || "",
    email: row.email || "",
    emailVerification: Boolean(row.emailVerificarion),
    dataConsentId:
      row.dataConsentId != null && row.dataConsentId !== ""
        ? String(row.dataConsentId)
        : "",
    dataConsent,
    positionOrderId: positionOrder?.id || "",
    positionOrder,
    dismissalOrderId: dismissalOrder?.id || "",
    dismissalOrder,
    jobDescriptionId: jobDescription?.id || "",
    jobDescription,
    employeePositionId: row.id != null ? String(row.id) : "",
  };
};

const mapPositionGroup = (
  group: EmployeeCardEmployeeWithGaps["positions"][number],
  actual: EmployeeCardPositionWithGaps[],
  index: number,
): TPositionFormItem => {
  const slotNumber =
    getEmployeePositionSlotByNumber(actual[0]?.number) ||
    defaultValuePosition(index).number;
  const positionId = group.positionId ?? group.position?.id;

  return {
    number: slotNumber,
    position: group.position
      ? {
          label: group.position.name,
          value: String(positionId ?? ""),
        }
      : null,
    educationTraining: group.educationTraining || undefined,
    educationCertificate: group.educationCertificate || undefined,
    licenseAssigned: false,
    licenseFreeCount: 0,
    directionId:
      group.directionId != null ? String(group.directionId) : undefined,
    directionRole: group.directionRole || [],
    dealers: actual.length ? actual.map(mapDealer) : [defaultValueDealer()],
    isNew: false,
  };
};

export const mapCardToForm = (
  employee: EmployeeCardEmployeeWithGaps,
): IEmployeeProfileForm => {
  const groups = (employee.positions || [])
    .map((group) => ({
      group,
      actual: getActualEmployeePositions(group.activePositions),
    }))
    .filter(({ actual }) => actual.length > 0);

  const hasMain = groups.some(({ actual }) => (actual[0]?.number || 1) === 1);
  const positions = groups
    .slice()
    .sort(
      (left, right) =>
        (left.actual[0]?.number || 1) - (right.actual[0]?.number || 1),
    )
    .map(({ group, actual }, index) => mapPositionGroup(group, actual, index));

  if (!hasMain) {
    positions.unshift(defaultValuePosition(0));
  }

  const photo = mapFile(employee.photo);

  return {
    id: employee.id,
    name: employee.name || "",
    surname: employee.surname || "",
    middleName: employee.middleName || "",
    noMiddleName: !employee.middleName,
    gender: employee.gender === false ? "Female" : "Male",
    birthDay: normalizeFormDate(employee.birthDay),
    workPhone: employee.workPhone || "",
    photoId: employee.photoId != null ? String(employee.photoId) : "",
    photo,
    ladaAcademiaId: employee.ladaAcademiaId || "",
    resourcesControlUserId: employee.resourcesControlUserId || "",
    createdAt: normalizeFormDate(employee.createdAt),
    lastEditDate:
      normalizeFormDate(employee.lastEditDate) ??
      normalizeFormDate(employee.createdAt),
    hiredDate: normalizeFormDate(employee.hiredDate),
    positions,
  };
};

export const getFirePositionsPayload = (
  data: IEmployeeProfileForm,
  employee: EmployeeCardEmployeeWithGaps,
): EmployeeFirePositionsParams => {
  const groups: Record<string, EmployeeFirePositionsItem> = {};

  (data.positions || []).forEach((position) => {
    const dealer = position.dealers[0];
    const endOrderNumber = (dealer?.endOrderNumber || "").trim();

    if (!dealer?.endDate || !endOrderNumber) {
      return;
    }

    const positionNumber = getEmployeePositionNumberBySlot(position.number);
    const positionId = Number(position.position?.value);
    const hasPositionToFire = (employee.positions || []).some(
      (group) =>
        group.positionId === positionId &&
        [
          ...getActualEmployeePositions(group.activePositions),
          ...getActualEmployeePositions(group.inactivePositions),
        ].some(
          (row) => row.number === positionNumber && row.status !== "fired",
        ),
    );

    if (!hasPositionToFire) {
      return;
    }

    const firedDate = parseDateString(dealer.endDate) as string;
    const dismissalOrderFileId = dealer.dismissalOrderId
      ? Number(dealer.dismissalOrderId)
      : undefined;
    const key = `${firedDate}|${endOrderNumber}|${dismissalOrderFileId ?? ""}`;

    if (!groups[key]) {
      groups[key] = {
        employeeId: Number(employee.id),
        positionNumbers: [],
        firedDate,
        endOrderNumber,
        ...(dismissalOrderFileId !== undefined ? { dismissalOrderFileId } : {}),
      };
    }

    groups[key].positionNumbers.push(positionNumber);
  });

  return { items: Object.keys(groups).map((key) => groups[key]) };
};

const mapFormPositionsToUpdate = (
  data: IEmployeeProfileForm,
): EmployeeCardUpdatePosition[] =>
  data.positions
    .map((position) => {
      if (
        !position.position?.value ||
        position.position.label === "Нет должности"
      ) {
        return undefined;
      }

      const dealer = position.dealers[0];
      const isPartner = dealer?.organizationType === "PARTNER";

      return {
        number: getEmployeePositionNumberBySlot(position.number),
        positionId: Number(position.position.value),
        dataConsentId: Number(dealer?.dataConsentId),
        bindingType: isPartner ? "PARTNER" : "DEALER",
        dealerId: isPartner ? null : Number(dealer?.dealer?.value),
        organizationId: isPartner
          ? dealer?.dealer?.value || undefined
          : undefined,
        startDate: dealer?.startDate
          ? parseDateString(dealer.startDate)
          : undefined,
        startOrderNumber: dealer?.startOrderNumber,
        email: dealer?.email || null,
        endDate:
          dealer?.endDate && String(dealer.endDate) !== "0"
            ? parseDateString(dealer.endDate)
            : null,
        endOrderNumber: dealer?.endOrderNumber || null,
        positionOrderId: dealer?.positionOrderId
          ? Number(dealer.positionOrderId)
          : null,
        dismissalOrderFileId: dealer?.dismissalOrderId
          ? Number(dealer.dismissalOrderId)
          : null,
        jobDescriptionId: dealer?.jobDescriptionId
          ? Number(dealer.jobDescriptionId)
          : null,
      };
    })
    .filter(Boolean) as EmployeeCardUpdatePosition[];

export const mapFormToCardUpdate = (
  data: IEmployeeProfileForm,
  employee: {
    email?: string | null;
    personalPhone?: string | null;
    firedDate?: string | null;
  },
): EmployeeCardUpdateParams => ({
  surname: data.surname,
  name: data.name,
  middleName: data.noMiddleName ? null : data.middleName || null,
  gender: data.gender === "Male",
  birthDay: data.birthDay ? parseDateString(data.birthDay) : null,
  email: employee.email || null,
  personalPhone: employee.personalPhone || null,
  workPhone: data.workPhone.replace(/\s/g, "") || null,
  hiredDate: data.hiredDate ? parseDateString(data.hiredDate) : null,
  ladaAcademiaId: data.ladaAcademiaId,
  resourcesControlUserId: data.resourcesControlUserId || null,
  photoId: data.photoId ? Number(data.photoId) : null,
  firedDate: employee.firedDate || null,
  positions: mapFormPositionsToUpdate(data),
});

export const mapFormToCreate = (
  data: IEmployeeProfileForm,
): EmployeeCreateParams => {
  const positions = data.positions
    .map((position) => {
      if (
        !position.position?.value ||
        position.position.label === "Нет должности"
      ) {
        return undefined;
      }

      const dealer = position.dealers[0];
      if (!dealer?.dealer?.value || !dealer.startDate) {
        return undefined;
      }

      const isPartner = dealer.organizationType === "PARTNER";

      const payload: EmployeeCreatePosition = {
        number: getEmployeePositionNumberBySlot(position.number),
        bindingType: isPartner ? "PARTNER" : "DEALER",
        dealerId: isPartner ? null : Number(dealer.dealer.value),
        organizationId: isPartner ? dealer.dealer.value : undefined,
        positionId: Number(position.position.value),
        startDate: parseDateString(dealer.startDate) as string,
      };

      if (dealer.email) {
        payload.email = dealer.email;
      }
      if (dealer.dataConsentId) {
        payload.dataConsentId = Number(dealer.dataConsentId);
      }
      if (dealer.startOrderNumber) {
        payload.startOrderNumber = dealer.startOrderNumber;
      }
      if (dealer.positionOrderId) {
        payload.positionOrderId = Number(dealer.positionOrderId);
      }

      return payload;
    })
    .filter(Boolean) as EmployeeCreatePosition[];

  const payload: EmployeeCreateParams = {
    surname: data.surname,
    name: data.name,
    gender: data.gender === "Male",
    middleName: data.noMiddleName ? undefined : data.middleName || undefined,
    birthDay: data.birthDay
      ? (parseDateString(data.birthDay) as string)
      : (parseDateString(new Date()) as string),
    workPhone: data.workPhone.replace(/\s/g, "") || undefined,
    ladaAcademiaId: data.ladaAcademiaId,
    resourcesControlUserId: data.resourcesControlUserId || undefined,
    hiredDate: data.hiredDate
      ? (parseDateString(data.hiredDate) as string)
      : (parseDateString(new Date()) as string),
    positions,
  };

  if (data.photoId) {
    payload.photoId = Number(data.photoId);
  }

  return payload;
};
